import React from "react";
import { Form, useField } from "react-final-form";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import { validateFilePackage } from "fond/fileValidation";
import { DropzoneField } from "fond/form/fields";
import { getFilesFromEvent } from "fond/utils/files";
import { Modal } from "fond/widgets";

interface UploadModalProps {
  layerTitle: string;
  onClose: () => void;
  onSubmit: (files: File[]) => void;
}

export interface UploadFormData {
  Files: File[];
}

const validateFiles = (files: File[] | undefined) => {
  if (!files || files.length === 0) {
    return "Please select files to upload";
  }

  const { isPackageValid, error } = validateFilePackage(files);
  if (!isPackageValid && error) {
    return error.message;
  }
  if (!isPackageValid) {
    return "Please upload all required shapefile components (.shp, .shx, .dbf, .prj)";
  }

  // ISG requires .shx file for GDAL/OGR processing
  const extensions = files.map((f) => f.name.split(".").pop()?.toLowerCase());
  if (!extensions.includes("shx")) {
    return "Please include the .shx file (spatial index) - required for ISG processing";
  }

  return undefined;
};

const FileList: React.FC = () => {
  const { input, meta } = useField<File[]>("Files");
  const files = input.value || [];
  const hasError = meta.error && meta.touched;

  if (files.length === 0) return null;

  const handleDelete = (fileName: string) => {
    const newFiles = files.filter((f) => f.name !== fileName);
    input.onChange(newFiles);
    input.onBlur();
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
      {files.map((file) => (
        <Chip
          key={file.name}
          label={file.name}
          size="small"
          icon={hasError ? <ErrorIcon /> : <CheckCircle />}
          color={hasError ? "error" : "success"}
          variant="outlined"
          onDelete={() => handleDelete(file.name)}
        />
      ))}
    </Box>
  );
};

const UploadModal: React.FC<UploadModalProps> = ({ layerTitle, onClose, onSubmit }: UploadModalProps) => {
  const handleSubmit = (values: UploadFormData) => {
    onSubmit(values.Files);
  };

  return (
    <Modal
      open
      variant="primary"
      header={`Upload ${layerTitle}`}
      content={
        <Box mt={2}>
          <Form<UploadFormData>
            validateOnBlur
            onSubmit={handleSubmit}
            subscription={{ submitting: true, valid: true, dirty: true }}
            render={({ handleSubmit, submitting, valid, dirty }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Box my={2} display="flex" flexDirection="column" rowGap={3}>
                    <Typography variant="body2" color="textSecondary">
                      Upload a shapefile package (.shp, .shx, .dbf, .prj) for the {layerTitle.toLowerCase()} layer.
                    </Typography>
                    <DropzoneField
                      name="Files"
                      acceptedFiles={{
                        "application/x-dbf": [".dbf"],
                        "application/unknown": [".prj", ".shp", ".shx", ".cpg"],
                      }}
                      fullWidth
                      message="Drop shapefile components here or click to browse."
                      getFilesFromEvent={getFilesFromEvent}
                      validate={validateFiles}
                    />
                    <FileList />
                    <ValidationMessage />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                      py: 3,
                    }}
                  >
                    <Button type="button" color="primary" onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
                      Cancel
                    </Button>
                    <LoadingButton type="submit" color="primary" loading={submitting} disabled={!dirty || !valid || submitting}>
                      Upload
                    </LoadingButton>
                  </Box>
                </form>
              );
            }}
          />
        </Box>
      }
      onClose={onClose}
      DialogActionsProps={{ sx: { display: "none" } }}
    />
  );
};

const ValidationMessage: React.FC = () => {
  const { meta } = useField<File[]>("Files");

  if (!meta.error || !meta.touched) return null;

  return (
    <Alert severity="error" variant="outlined">
      {meta.error}
    </Alert>
  );
};

export default UploadModal;
